<template>
  <aside :class="['main-sidebar', sidebarVisible ? 'open' : '']">
    <div
      class="mobile-backdrop"
      @click="handleCloseSidebar">
    </div>
    <div class="nav-wrapper">
      <d-nav class="flex-column pb-5">
        <li
          v-for="(section, sectionIdx) in finalSidebarItems"
          :key="sectionIdx"
          class="nav-item dropdown">
          <d-link
            :class="['nav-link', 'd-flex', 'dropdown-toggle']"
            href="#"
            v-d-toggle="`snc-${sectionIdx}`">
            <div class="item-icon-wrapper" v-if="section.htmlBefore" v-pure-html="section.htmlBefore" />
            <span v-if="section.title">{{ section.title[currentLanguage] }}</span>

            <d-badge
              v-if="section.aggregatedCounter"
              :theme="section.aggregatedCounter === 0 ? 'light' : 'primary'"
              class="ml-auto mr-1" >
              {{ section.aggregatedCounter }}
            </d-badge>

            <div class="item-icon-wrapper" v-if="section.htmlAfter" v-pure-html="section.htmlAfter" />
          </d-link>

          <d-collapse
            :id="`snc-${sectionIdx}`"
            class="dropdown-menu dropdown-menu-small"
            accordion="sidebar-items-accordion">
            <template v-for="(item, itemIdx) in section.items">
              <div
                v-if="item.items && item.items.length"
                :key="`group-${itemIdx}`"
                class="sidebar-group">
                <d-dropdown-item
                  :to="item.to"
                  class="sidebar-group-header d-flex align-items-center">
                  <strong>{{ item.title[currentLanguage] }}</strong>
                </d-dropdown-item>

                <d-dropdown-item
                  v-for="(subItem, subItemIdx) in item.items"
                  :key="`sub-${subItemIdx}`"
                  :to="subItem.to"
                  class="sidebar-sub-item d-flex align-items-center">
                  <span class="sub-item-text">{{ subItem.title[currentLanguage] }}</span>

                  <d-badge
                    v-if="subItem.counter"
                    :theme="counters[item.counter] === 0 ? 'light' : 'primary'"
                    class="ml-auto">
                    {{ counters[subItem.counter] }}
                  </d-badge>
                </d-dropdown-item>

                <div
                  class="dropdown-divider">
                </div>
              </div>

              <d-dropdown-item
                v-else
                :key="itemIdx"
                :href="item.href"
                :to="item.to"
                class="d-flex align-items-center">
                {{ item.title[currentLanguage] }}

                <d-badge
                  v-if="item.counter"
                  :theme="counters[item.counter] === 0 ? 'light' : 'primary'"
                  class="ml-auto">
                  {{ counters[item.counter] }}
                </d-badge>
              </d-dropdown-item>
            </template>
          </d-collapse>
        </li>
      </d-nav>
    </div>
  </aside>
</template>

<script>
import { mapGetters } from 'vuex';
import getPrefferedLanguage from '@/utils/i18nUtils.js';

export default {
  name: 'main-sidebar',
  props: {
    items: {
      type: Array,
      required: true,
    }
  },
  computed: {
    ...mapGetters([
      'getUserEmail'
    ]),
    userIsWarehouseman () {
      return this.$store.getters.getUserType === 'warehouse_manager';
    },
    userIsRealWarehouseman () {
      return this.$store.getters.getUserType === 'warehouseman';
    },
    userIsCustomer () {
      return this.$store.getters.getUserType === 'customer';
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    currentLanguage() {
      return getPrefferedLanguage();
    },
    finalSidebarItems() {
      const isItemVisible = (item) => {
        if (item.to === '/cele-pracownikow' && !this.$showKpiArea) return false;
        if (item.to === '/przesylki' && !this.$showUpsIntegration) return false;
        if (item.to === '/zamowienia-hafciarnia' && !this.$showHafciarniaIntegration) return false;

        if (item.userType) {
          if (item.userType === 'admin' && !this.userIsAdmin) return false;
          if (item.userType === 'not-customer-and-warehouseman' && (this.userIsWarehouseman || this.userIsCustomer)) return false;
          if (item.userType === 'warehouseman' && !this.userIsWarehouseman) return false;
          if (item.userType === 'customer' && !this.userIsCustomer) return false;
          if (item.userType === 'not-customer' && this.userIsCustomer) return false;
          if (item.userType === 'not-warehouseman' && this.userIsWarehouseman) return false;
        }
        return true;
      };

      const filterRecursive = (items) => items.reduce((acc, item) => {
        if (!isItemVisible(item)) return acc;

        const newItem = { ...item };
        let currentItemCount = 0;

        if (newItem.counter && this.counters[newItem.counter]) {
          currentItemCount = parseInt(this.counters[newItem.counter], 10) || 0;
        }

        if (newItem.items && newItem.items.length > 0) {
          newItem.items = filterRecursive(newItem.items);
          const childrenSum = newItem.items.reduce((sum, child) => sum + (child.aggregatedCounter || 0), 0);

          newItem.aggregatedCounter = currentItemCount + childrenSum;
        } else {
          newItem.aggregatedCounter = currentItemCount;
        }

        if (newItem.to || (newItem.items && newItem.items.length > 0)) {
          acc.push(newItem);
        }
        return acc;
      }, []);

      return filterRecursive(this.items);
    }
  },
  data() {
    return {
      sidebarVisible: false,
      counters: {}
    };
  },
  created() {
    this.$bus.$on('toggle-sidebar', this.handleToggleSidebar);
    this.$bus.$on('close-sidebar', this.handleCloseSidebar);
  },
  mounted() {
    this.refreshCounters();
    this.$bus.$on('sidebar-counters-refresh', this.refreshCounters);
  },
  beforeDestroy() {
    this.$bus.$off('toggle-sidebar');
    this.$bus.$off('close-sidebar');
    this.$bus.$off('sidebar-counters-refresh', this.refreshCounters);
  },
  methods: {
    handleToggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    handleCloseSidebar() {
      this.sidebarVisible = false;
    },
    refreshCounters () {
      if (this.userIsCustomer || this.userIsWarehouseman) {
        return;
      }
      this.$http.get('/orders/count').then(response => {
        if (!response.data) return;
        this.counters = JSON.parse(JSON.stringify(response.data));
      }).catch(error => {
        console.log(error);
      });
    }
  }
};
</script>
